import { isNull, isUndefined } from 'lodash';
import * as api from './adapter';
import { convertItems, convertItemJson } from './convertors/timeplans';
import { dateToFormat } from '../DateTime';

const include = 'user';
const fields = {
  users: 'name,surname,avatar,readiness_date,promotions_count',
};

// Exported for the unit test in `__tests__/timeplans.test.js`. This
// builder serves BOTH the slot form and drag/resize on the board, and the
// difference between them is exactly which keys the payload carries — a
// contract worth pinning directly rather than through a fetch mock.
export const getTimeplanRequestBody = (formValues, id) => {
  const {
    userId,
    port,
    start,
    end,
    travel_start,
    travel_end,
    status,
    resource,
    offer,
    vacancy_description,
    to_publish,
    external_only,
    newStatusId,
    poolRemoval,
    repair_team
  } = formValues;
  const manningScaleId = resource && resource.split(':')[0];

  // A date we could not format is a date we must not send. Before this, an
  // unparseable value arrived here as Luxon's "Invalid DateTime" string, the
  // backend cast it to NULL and blew up publishing the vacancy (500, slot
  // rolled back). Omitting the key lets the backend's own validation answer
  // properly instead.
  const startDate = dateToFormat(start);
  const endDate = dateToFormat(end);

  const body = {
    type: 'time_plans',
    attributes: {
      to_publish,
      repair_team,
      external_only,
    },
  };

  if (startDate) body.attributes.start = startDate;
  if (endDate) body.attributes.end = endDate;

  if (id) body.id = String(id);
  if (status) body.attributes.status = status.value;
  if (newStatusId) body.attributes.newStatusId = newStatusId;
  // CREW-1283 — the pool comes off in the same transaction as the status.
  if (newStatusId && poolRemoval) body.attributes.poolRemoval = poolRemoval;

  // Travel dates (CREW-1184) are written only when the caller actually
  // carries the key. This same builder serves drag/resize on the board
  // (`onEventChange` sends `{ id, start, end, resource, … }` and nothing
  // else), so an unconditional write would put the field in the payload
  // for a move that never saw the form.
  //
  // The test is `!isUndefined`, NOT truthiness: clearing the picker sets
  // the value to `null`, and that `null` has to reach the API to erase a
  // stored date. `if (travel_start)` would silently make the field
  // un-clearable.
  //
  // No `dateToFormat` here — `withApiDates` (adapter.js) already turns any
  // `Date` in the body into its calendar day, and an untouched value is
  // still the bare "YYYY-MM-DD" the API served.
  if (!isUndefined(travel_start)) body.attributes.travel_start = travel_start;
  if (!isUndefined(travel_end)) body.attributes.travel_end = travel_end;

  if (status && status.value === 'vacancy') {
    if (offer) body.attributes.offer = offer;
    if (vacancy_description) body.attributes.vacancy_description = vacancy_description;
  }

  if (!isUndefined(userId) || !isUndefined(port) || manningScaleId) body.relationships = {};

  if (userId) {
    body.relationships.user = {
      data: {
        id: userId,
        type: 'users'
      }
    };
  }
  if (isNull(userId)) body.relationships.user = { data: null };
  if (manningScaleId) {
    body.relationships.manning_scale = {
      data: {
        id: manningScaleId,
        type: 'manning_scales'
      }
    };
  }
  if (isNull(port)) body.relationships.port = { data: null };

  return body;
};

export default {
  /**
   * checkHasAtLeastOne
   * @returns {Promise<*>}
   */
  index: async ({
    state: {
      viewport: {
        msIds,
        start,
        end
      }
    }
  }) => {
    const { data } = await api.read('time_plans', {
      query: {
        include,
        fields,
        filter: {
          date_range: `${start},${end}`,
          manning_scale_id: msIds
        },
      },
    });

    return convertItems(data);
  },
  show: async (options = {}) => {
    const { data } = await api.read('time_plans', {
      query: {
        include,
        fields,
        ...options,
      },
    });

    return convertItems(data);
  },
  /**
   * create
   * @param formValues
   * @returns {Promise<{relationships, role, comments, vessel, attributes, id, type, user}>}
   */
  create: async (formValues) => {
    const { data } = await api.create(
      getTimeplanRequestBody(formValues),
      { query: { include } },
    );

    return convertItemJson(data);
  },
  /**
   * update
   * @param id
   * @param formValues
   * @returns {Promise<{relationships, role, comments, vessel, attributes, id, type, user}>}
   */
  update: async (id, formValues) => {
    const { data } = await api.update(
      getTimeplanRequestBody(formValues, id),
      { query: { include } },
    );

    return convertItemJson(data);
  },
  /**
   * delete
   * @param id
   * @returns {Promise}
   */
  delete: (id) => api.destroy({
    id,
    type: 'time_plans'
  }),

  /**
   * Slots this seafarer could be moved to (CREW-1172). Blocked ones come back
   * too, each with `blocked_reason` — the customer asked for the reason to be
   * visible rather than for the option to disappear.
   * @param {Number|String} id source time plan id
   * @returns {Promise<{targets: Array}>}
   */
  reassignmentTargets: async (id, { includeMismatched = false } = {}) => {
    // CREW-1207: by default the backend returns only slots the seafarer could
    // actually take (matching rank and Owners Pool). `includeMismatched`
    // restores the full list with its refusal reasons.
    const query = includeMismatched ? "?include_mismatched=true" : "";
    const { data } = await api.apiRequest(
      `/api/time_plans/${id}/reassignment_targets${query}`
    );

    return data;
  },

  /**
   * Move the seafarer to another slot (CREW-1172/1173).
   * @param {Number|String} id source time plan id
   * @param {Number|String} targetTimePlanId
   * @returns {Promise<{time_plan_id: Number}>}
   */
  reassign: async (id, targetTimePlanId) => {
    const { data } = await api.apiRequest(`/api/time_plans/${id}/reassignment`, {
      method: 'POST',
      body: JSON.stringify({ target_time_plan_id: targetTimePlanId }),
    });

    return data;
  },

  printReport: async (payload) => {
    const {
      vessels,
      roles,
      date
    } = payload;
    const defaultOptions = api.getDefaultOptions();
    const url = `/api/print/time_plan?date=${date}&vessels=${vessels}&roles=${roles}`;
    const response = await fetch(url, defaultOptions);
    const blob = await response.blob();
    const filename = response.headers.get('Content-Disposition')
      .split('filename=')[1].replaceAll('"', '');
    const uri = window.URL.createObjectURL(blob);

    return {
      uri,
      name: filename,
    };
  },
};
